/**
 * ATLAS MT5 Position Sizing Calculator — calculation core
 * No guessed broker presets. Returns estimated sizing only after manual verification.
 * Educational estimator; NOT a substitute for MT5 OrderCalcProfit / OrderCalcMargin.
 */

export class RiskInputError extends Error {
  constructor(message, code = 'INVALID_INPUT') { super(message); this.name = 'RiskInputError'; this.code = code; }
}

function positive(value, label) {
  if (value == null || String(value).trim() === '') throw new RiskInputError(`${label} is required.`);
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) throw new RiskInputError(`${label} must be greater than zero.`);
  return num;
}
function nonnegative(value, label, fallback = 0) {
  if (value == null || String(value).trim() === '') return fallback;
  const num = Number(value);
  if (!Number.isFinite(num) || num < 0) throw new RiskInputError(`${label} cannot be negative.`);
  return num;
}
const round12 = n => Number(n.toPrecision(12));

/**
 * @param {Object} i Raw input. Money fields and tickValueLoss are all in the SAME account currency.
 * @param {'tick'|'mt5quote'} i.method
 * - tick: uses tick size and losing-side tick value for 1.00 lot.
 * - mt5quote: uses externally obtained account-currency losing P/L from MT5 OrderCalcProfit at
 *   given entry/stop for probeVolume lots. This is a quote, not a live connection.
 */
export function calculatePosition(i) {
  if (!i || typeof i !== 'object') throw new RiskInputError('Enter the trade details.');
  if (i.verified !== true) throw new RiskInputError('Verify the exact MT5 symbol specification and check the confirmation box.', 'UNVERIFIED');
  const accountValue = positive(i.accountValue, 'Account value');
  const riskPercent = positive(i.riskPercent, 'Risk percentage');
  if (riskPercent > 100) throw new RiskInputError('Risk percentage cannot exceed 100%.');
  const entry = positive(i.entry, 'Expected entry price');
  const stop = positive(i.stop, 'Stop-loss price');
  if (!['buy', 'sell'].includes(i.side)) throw new RiskInputError('Select Buy or Sell.');
  if (i.side === 'buy' && !(stop < entry)) throw new RiskInputError('A buy stop-loss must be below the entry price.');
  if (i.side === 'sell' && !(stop > entry)) throw new RiskInputError('A sell stop-loss must be above the entry price.');

  const minLot = positive(i.minLot, 'Minimum volume');
  const maxLot = positive(i.maxLot, 'Maximum volume');
  const volumeStep = positive(i.volumeStep, 'Volume step');
  if (maxLot < minLot) throw new RiskInputError('Maximum volume must be at least the minimum volume.');
  const distance = Math.abs(entry - stop);
  const riskBudget = accountValue * riskPercent / 100;
  const extraTicks = nonnegative(i.extraTicks, 'Additional adverse-movement allowance');
  const commissionPerLot = nonnegative(i.commissionPerLot, 'Round-trip commission per lot');
  const method = i.method ?? 'tick';
  let lossPerLotExCosts, tickSize = null, tickValueLoss = null, distanceTicks = null;

  if (method === 'tick') {
    tickSize = positive(i.tickSize, 'Tick size');
    tickValueLoss = positive(i.tickValueLoss, 'Losing-side tick value in account currency');
    distanceTicks = distance / tickSize;
    lossPerLotExCosts = distanceTicks * tickValueLoss;
  } else if (method === 'mt5quote') {
    const probeVolume = positive(i.probeVolume, 'MT5 quote volume');
    const quoteLoss = positive(i.quoteLoss, 'MT5 quoted loss (absolute amount)');
    if (probeVolume < minLot - 1e-10 || probeVolume > maxLot + 1e-10) {
      throw new RiskInputError('MT5 quoted volume must be within the symbol’s volume limits.');
    }
    lossPerLotExCosts = quoteLoss / probeVolume;
    if (extraTicks > 0) {
      tickSize = positive(i.tickSize, 'Tick size');
      tickValueLoss = positive(i.tickValueLoss, 'Losing-side tick value');
    }
  } else throw new RiskInputError('Choose a valid calculation method.');

  // Extra ticks are an additional conservative adverse-price allowance, not a guaranteed gap limit.
  const movementBufferPerLot = extraTicks * (tickValueLoss ?? 0);
  const riskPerLot = lossPerLotExCosts + movementBufferPerLot + commissionPerLot;
  if (!Number.isFinite(riskPerLot) || riskPerLot <= 0) throw new RiskInputError('Risk per lot could not be determined.');

  // Volume sequence: minLot, minLot + step, ..., <= maxLot. Round DOWN only.
  const rawLots = riskBudget / riskPerLot;
  let capped = Math.min(rawLots, maxLot);
  const warnings = [];
  if (riskPercent > 2) warnings.push('Risk is above 2% of your selected account amount.');
  if (extraTicks === 0) warnings.push('No additional adverse-price buffer entered; gaps and slippage can exceed this estimate.');
  if (commissionPerLot === 0) warnings.push('Commission is set to zero; confirm whether your account charges it.');
  const minRisk = minLot * riskPerLot;
  let lots = 0;
  if (capped + 1e-10 >= minLot) {
    const k = Math.max(0, Math.floor((capped - minLot) / volumeStep + 1e-9));
    lots = round12(minLot + k * volumeStep);
    // Belt-and-braces floating precision safeguard.
    while (lots > 0 && (lots > maxLot + 1e-10 || lots * riskPerLot > riskBudget + 1e-8)) {
      const previous = lots - volumeStep;
      lots = previous < minLot - 1e-10 ? 0 : round12(previous);
    }
  }
  const noTrade = lots === 0;
  const estimatedRisk = lots * riskPerLot;
  const actualPct = noTrade ? 0 : estimatedRisk / accountValue * 100;

  // Margin is deliberately separate from stop-loss exposure.
  let margin = {status:'not-checked', required:null, free:null, sufficient:null};
  const freeMarginPresent = !(i.freeMargin == null || String(i.freeMargin).trim() === '');
  const marginPerLotPresent = !(i.marginPerLot == null || String(i.marginPerLot).trim() === '');
  if (freeMarginPresent !== marginPerLotPresent) {
    warnings.push('Margin check incomplete: enter BOTH available free margin and estimated margin per lot.');
  } else if (freeMarginPresent && marginPerLotPresent) {
    const free = nonnegative(i.freeMargin, 'Free margin');
    const marginPerLot = positive(i.marginPerLot, 'Margin per lot');
    const required = lots * marginPerLot;
    margin = {status: 'estimate', required, free, sufficient: noTrade ? null : required <= free + 1e-8};
    if (!noTrade && !margin.sufficient) warnings.push('Estimated required margin exceeds entered free margin. This size is not affordable under the entered margin assumptions.');
  } else warnings.push('Margin has not been checked. Confirm it in MT5.');

  return {
    method, noTrade, riskBudget, rawLots, lots,
    estimatedRisk, actualPct, minRisk,
    riskPerLot, lossPerLotExCosts, movementBufferPerLot, commissionPerLot,
    distance, distanceTicks, margin, warnings,
    estimateOnly: true, verifiedManually: true,
  };
}
